import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  buildOrderDeskPackingSlipHTML,
  PACKING_SLIP_DEFAULT_LOGO_URL,
} from '../../utils/packingSlipHtml';
import { buildCheetahLz17PackingSlipHTML } from '../../utils/cheetahLz17PackingSlipHtml';
import { buildGo4PrintDocuments } from '../../utils/buildGo4PrintDocuments';
import { enrichOrdersWithOrderDeskCheckoutDates } from '../../utils/enrichOrdersWithOrderDeskCheckoutDates';
import { formatPrintingDeliveryDisplay } from '../../utils/printingToolkitFilterOptions';
import { getOrderDeliveryDate } from '../../utils/orderDateUtils';
import { getOrderPrintSourceId } from '../../services/packingSlipPrintCountService';
import {
  buildShippingLabelPreviewBlobForOrder,
  findShippingLabelAttachesForOrder,
} from '../../utils/shippingLabelPreview';
import { PACKING_SLIP_FORMAT } from '../../utils/packingSlipPrintDefaults';
import './PrintPreviewModal.css';

const A4_WIDTH_PX = 794;
const A4_HEIGHT_PX = 1123;
/** GoDEX / carrier label ~100×150 mm at ~96 dpi */
const LABEL_WIDTH_PX = 378;
const LABEL_HEIGHT_PX = 567;

function getQuantityTotal(order) {
  const items =
    order?.order_items ||
    order?.items ||
    order?.originalItems ||
    [];
  return items.reduce((sum, item) => {
    const qty = Number(item?.quantity ?? item?.qty ?? item?.Quantity ?? 0);
    return sum + (Number.isFinite(qty) ? qty : 0);
  }, 0);
}

function usePreviewScale(stageRef, pageWidth, pageHeight, deps) {
  const [scale, setScale] = useState(0.55);
  useEffect(() => {
    const node = stageRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const update = () => {
      const width = node.clientWidth || pageWidth;
      const height = node.clientHeight || pageHeight;
      const next = Math.min(width / pageWidth, height / pageHeight, 1);
      setScale(Math.max(0.28, next * 0.96));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps array from caller
  }, deps);
  return scale;
}

export default function PrintPreviewModal({
  order,
  settings,
  logoUrl = PACKING_SLIP_DEFAULT_LOGO_URL,
  filterDate = '',
  shipLabelAttaches = [],
  onClose,
  onPrint,
}) {
  const hasShipLabel = useMemo(
    () => findShippingLabelAttachesForOrder(order, shipLabelAttaches).length > 0,
    [order, shipLabelAttaches],
  );
  const [format, setFormat] = useState(PACKING_SLIP_FORMAT.STANDARD);
  const [go4Pane, setGo4Pane] = useState('a4');
  const [html, setHtml] = useState('');
  const [go4OutsideHtml, setGo4OutsideHtml] = useState('');
  const [shipPdfUrl, setShipPdfUrl] = useState('');
  const [shipMeta, setShipMeta] = useState(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [enrichedOrder, setEnrichedOrder] = useState(order);
  const stageRef = useRef(null);
  const go4A4Ref = useRef(null);
  const go4OutsideRef = useRef(null);

  const orderKey = useMemo(
    () => getOrderPrintSourceId(order) || order?.id || '',
    [order],
  );

  const showPrices = settings?.showPrices !== false;
  const showSignatureLines = settings?.showSignatureLines !== false;
  const previewLogo = settings?.logoUrl || logoUrl || PACKING_SLIP_DEFAULT_LOGO_URL;
  const settingsKey = useMemo(
    () =>
      JSON.stringify({
        showPrices,
        showSignatureLines,
        includeDuplicate: settings?.includeDuplicate !== false,
        showKgLines: settings?.showKgLines !== false,
        paperSize: settings?.paperSize || 'A4',
        orientation: settings?.orientation || 'portrait',
        margins: settings?.margins || null,
      }),
    [settings, showPrices, showSignatureLines],
  );

  const isShipping = format === 'shipping';
  const isGo4 = format === PACKING_SLIP_FORMAT.GO4;
  const pageWidth = isShipping || isGo4 ? LABEL_WIDTH_PX : A4_WIDTH_PX;
  const pageHeight = isShipping || isGo4 ? LABEL_HEIGHT_PX : A4_HEIGHT_PX;
  const singleScale = usePreviewScale(stageRef, isShipping ? LABEL_WIDTH_PX : A4_WIDTH_PX, isShipping ? LABEL_HEIGHT_PX : A4_HEIGHT_PX, [
    html,
    shipPdfUrl,
    busy,
    format,
  ]);
  const go4A4Scale = usePreviewScale(go4A4Ref, A4_WIDTH_PX, A4_HEIGHT_PX, [
    html,
    busy,
    format,
    go4Pane,
  ]);
  const go4OutsideScale = usePreviewScale(
    go4OutsideRef,
    LABEL_WIDTH_PX,
    LABEL_HEIGHT_PX,
    [go4OutsideHtml, busy, format, go4Pane],
  );

  useEffect(() => {
    if (!hasShipLabel && format === 'shipping') {
      setFormat(PACKING_SLIP_FORMAT.STANDARD);
    }
  }, [hasShipLabel, format]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = '';
    const run = async () => {
      setBusy(true);
      setError('');
      setShipPdfUrl('');
      setShipMeta(null);
      setGo4OutsideHtml('');
      try {
        if (format === 'shipping') {
          const preview = await buildShippingLabelPreviewBlobForOrder(
            order,
            shipLabelAttaches,
          );
          if (cancelled) return;
          if (!preview?.blob) {
            setError('No shipping label pages are attached for this order.');
            setHtml('');
            return;
          }
          objectUrl = URL.createObjectURL(preview.blob);
          setShipPdfUrl(objectUrl);
          setShipMeta({
            pageCount: preview.pageCount,
            fileName: preview.fileName,
          });
          setHtml('');
          setEnrichedOrder(order);
          return;
        }

        const [enriched] = await enrichOrdersWithOrderDeskCheckoutDates([order], {
          filterDate: filterDate || undefined,
          prefer: 'both',
        });
        if (cancelled) return;
        const working = enriched || order;
        setEnrichedOrder(working);

        if (format === PACKING_SLIP_FORMAT.GO4) {
          const parts = await buildGo4PrintDocuments([working], {
            logoUrl: previewLogo,
            settings: settings || {},
            showSignatureLines,
          });
          if (cancelled) return;
          setHtml(parts.slipHtml);
          setGo4OutsideHtml(parts.outsideHtml);
          return;
        }

        const nextHtml =
          format === PACKING_SLIP_FORMAT.CHEETAH
            ? await buildCheetahLz17PackingSlipHTML([working], {
                logoUrl: previewLogo,
                enrichAccounts: true,
                showPrices,
                showSignatureLines,
              })
            : await buildOrderDeskPackingSlipHTML([working], {
                logoUrl: previewLogo,
                settings: settings || {},
              });
        if (cancelled) return;
        setHtml(nextHtml);
      } catch (err) {
        if (!cancelled) {
          setHtml('');
          setGo4OutsideHtml('');
          setShipPdfUrl('');
          setError(
            err?.message ||
              (format === 'shipping'
                ? 'Unable to build shipping label preview.'
                : 'Unable to build packing slip preview.'),
          );
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    };
    run();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [
    order,
    format,
    settingsKey,
    previewLogo,
    filterDate,
    showPrices,
    showSignatureLines,
    settings,
    shipLabelAttaches,
  ]);

  const company =
    enrichedOrder?.companyName ||
    enrichedOrder?.customerCompany ||
    enrichedOrder?.customer?.company ||
    enrichedOrder?.shipping?.company ||
    '—';
  const delivery =
    formatPrintingDeliveryDisplay(enrichedOrder) ||
    getOrderDeliveryDate(enrichedOrder) ||
    '—';
  const shipping =
    enrichedOrder?.shipping_method ||
    enrichedOrder?.shippingMethod ||
    enrichedOrder?.shipping?.method ||
    '—';
  const itemCount = (
    enrichedOrder?.order_items ||
    enrichedOrder?.items ||
    enrichedOrder?.originalItems ||
    []
  ).length;

  const renderScaledFrame = ({
    ref,
    title,
    srcDoc,
    width,
    height,
    scale,
  }) => (
    <div
      className="print-preview-stage-pane"
      ref={ref}
    >
      <div
        className="print-preview-scale"
        style={{
          width: width * scale,
          height: height * scale,
        }}
      >
        <iframe
          className="print-preview-frame"
          title={title}
          srcDoc={srcDoc}
          style={{
            width,
            height,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        />
      </div>
    </div>
  );

  return (
    <div className="modal-overlay print-preview-overlay" role="dialog" aria-modal="true">
      <div className="modal-content print-preview-modal">
        <div className="modal-header print-preview-modal__header">
          <div>
            <h2>Print preview</h2>
            <p>Order {orderKey || '—'}</p>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close preview">
            ×
          </button>
        </div>

        <div className="print-preview-modal__toolbar">
          <div className="print-preview-format" role="tablist" aria-label="Preview format">
            <button
              type="button"
              role="tab"
              aria-selected={format === PACKING_SLIP_FORMAT.STANDARD}
              className={`tab-pill tab-pill--secondary ${format === PACKING_SLIP_FORMAT.STANDARD ? 'active' : ''}`}
              onClick={() => setFormat(PACKING_SLIP_FORMAT.STANDARD)}
            >
              Standard A4
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={format === PACKING_SLIP_FORMAT.CHEETAH}
              className={`tab-pill tab-pill--secondary ${format === PACKING_SLIP_FORMAT.CHEETAH ? 'active' : ''}`}
              onClick={() => setFormat(PACKING_SLIP_FORMAT.CHEETAH)}
            >
              Cheetah LZ17
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={format === PACKING_SLIP_FORMAT.GO4}
              className={`tab-pill tab-pill--secondary ${format === PACKING_SLIP_FORMAT.GO4 ? 'active' : ''}`}
              onClick={() => setFormat(PACKING_SLIP_FORMAT.GO4)}
            >
              GO4
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={format === 'shipping'}
              className={`tab-pill tab-pill--secondary ${format === 'shipping' ? 'active' : ''}`}
              disabled={!hasShipLabel}
              title={
                hasShipLabel
                  ? 'View the attached carrier shipping label'
                  : 'No team shipping-label attach for this order yet'
              }
              onClick={() => {
                if (hasShipLabel) setFormat('shipping');
              }}
            >
              Shipping label
            </button>
          </div>
          <div className="print-preview-meta-inline">
            <span>{company}</span>
            <span>{delivery}</span>
            <span>{shipping}</span>
            {isShipping ? (
              <span>
                {shipMeta?.pageCount || 0} label page
                {(shipMeta?.pageCount || 0) === 1 ? '' : 's'}
                {shipMeta?.fileName ? ` · ${shipMeta.fileName}` : ''}
              </span>
            ) : isGo4 ? (
              <span>A4 slip + GoDEX outside 3.94×5.91</span>
            ) : (
              <>
                <span>
                  {itemCount} item{itemCount === 1 ? '' : 's'} · qty{' '}
                  {getQuantityTotal(enrichedOrder)}
                </span>
                <span>
                  {settings?.copiesPerOrder || 1} label
                  {(settings?.copiesPerOrder || 1) === 1 ? '' : 's'}
                </span>
              </>
            )}
          </div>
        </div>

        <div className="print-preview-modal__body">
          {isGo4 ? (
            <>
              <div className="print-preview-go4-tabs" role="tablist" aria-label="GO4 panes">
                <button
                  type="button"
                  role="tab"
                  aria-selected={go4Pane === 'a4'}
                  className={`tab-pill tab-pill--secondary ${go4Pane === 'a4' ? 'active' : ''}`}
                  onClick={() => setGo4Pane('a4')}
                >
                  A4 slip
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={go4Pane === 'outside'}
                  className={`tab-pill tab-pill--secondary ${go4Pane === 'outside' ? 'active' : ''}`}
                  onClick={() => setGo4Pane('outside')}
                >
                  GoDEX outside
                </button>
              </div>
              {busy ? (
                <p className="print-preview-status">Building GO4 preview…</p>
              ) : error ? (
                <p className="print-preview-status print-preview-status--error">{error}</p>
              ) : (
                <div className="print-preview-go4-panes">
                  <div
                    className={`print-preview-go4-pane${go4Pane === 'a4' ? ' is-active' : ''}`}
                  >
                    <p className="print-preview-go4-pane__label">A4 packing slip</p>
                    {renderScaledFrame({
                      ref: go4A4Ref,
                      title: `GO4 A4 preview ${orderKey}`,
                      srcDoc: html,
                      width: A4_WIDTH_PX,
                      height: A4_HEIGHT_PX,
                      scale: go4A4Scale,
                    })}
                  </div>
                  <div
                    className={`print-preview-go4-pane${go4Pane === 'outside' ? ' is-active' : ''}`}
                  >
                    <p className="print-preview-go4-pane__label">GoDEX outside</p>
                    {renderScaledFrame({
                      ref: go4OutsideRef,
                      title: `GO4 outside preview ${orderKey}`,
                      srcDoc: go4OutsideHtml,
                      width: LABEL_WIDTH_PX,
                      height: LABEL_HEIGHT_PX,
                      scale: go4OutsideScale,
                    })}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div
              className={`print-preview-stage${isShipping ? ' print-preview-stage--label' : ''}`}
              ref={stageRef}
            >
              {busy ? (
                <p className="print-preview-status">
                  {isShipping ? 'Loading shipping label…' : 'Building packing slip…'}
                </p>
              ) : error ? (
                <p className="print-preview-status print-preview-status--error">{error}</p>
              ) : isShipping && shipPdfUrl ? (
                <div
                  className="print-preview-scale print-preview-scale--label"
                  style={{
                    width: pageWidth * singleScale,
                    height: pageHeight * singleScale,
                  }}
                >
                  <iframe
                    className="print-preview-frame print-preview-frame--pdf"
                    title={`Shipping label preview ${orderKey}`}
                    src={shipPdfUrl}
                    style={{
                      width: pageWidth,
                      height: pageHeight,
                      transform: `scale(${singleScale})`,
                      transformOrigin: 'top left',
                    }}
                  />
                </div>
              ) : (
                <div
                  className="print-preview-scale"
                  style={{
                    width: A4_WIDTH_PX * singleScale,
                    height: A4_HEIGHT_PX * singleScale,
                  }}
                >
                  <iframe
                    className="print-preview-frame"
                    title={`Packing slip preview ${orderKey}`}
                    srcDoc={html}
                    style={{
                      width: A4_WIDTH_PX,
                      height: A4_HEIGHT_PX,
                      transform: `scale(${singleScale})`,
                      transformOrigin: 'top left',
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-actions print-preview-modal__actions">
          <button type="button" className="toolkit-btn toolkit-btn--ghost" onClick={onClose}>
            Close
          </button>
          {isShipping ? (
            <button type="button" className="toolkit-btn toolkit-btn--primary" onClick={onClose}>
              Done
            </button>
          ) : (
            <button type="button" className="toolkit-btn toolkit-btn--primary" onClick={onPrint}>
              Prepare print…
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
